import type { Experiment } from 'types';
import { NavLink } from '@/components/NavLink';
import { ExternalLink, GripVertical, X } from 'lucide-react';
import { cn } from 'helpers/cn';
import { FOCUS_RING } from '../../ConsolePanel';

type ExperimentCardProps = {
  experiment: Experiment;
  /** A card in a stage that has already run: shown, never moved. */
  frozen?: boolean;
  /** Highlighted when this card is one half of a collision. */
  flagged?: 'warn' | 'block' | null;
  onRemove?: () => void;
  onDragStart: (ev: React.DragEvent) => void;
  /** Set on one card only, for the product tour. */
  tourAnchor?: string;
};

export const ExperimentCard = ({
  experiment,
  frozen,
  flagged,
  onRemove,
  onDragStart,
  tourAnchor,
}: ExperimentCardProps) => (
  <div
    data-tour={tourAnchor}
    draggable={!frozen}
    onDragStart={onDragStart}
    className={cn(
      'group border-line bg-panel2 rounded-card border p-2',
      !frozen && 'cursor-grab active:cursor-grabbing',
      frozen && 'opacity-75',
      flagged === 'block' && 'border-danger-line',
      flagged === 'warn' && 'border-warn-line',
    )}
  >
    <div className="flex items-start gap-1.5">
      {!frozen && <GripVertical className="text-dim mt-0.5 size-3.5 shrink-0" strokeWidth={1.5} />}
      <div className="min-w-0 flex-1">
        <p className="truncate text-body font-medium" title={experiment.name}>
          {experiment.name}
        </p>
        <div className="micro-label mt-1 flex items-center gap-2">
          <span className="tracking-normal">#{experiment.id}</span>
          <span>{experiment.status}</span>
          <NavLink
            to={`/experiments/${experiment.id}`}
            // focus-visible:opacity-100 is not optional: without it, tabbing into
            // this lands the focus ring on a fully transparent link, so a keyboard
            // user cannot see where they are. max-md keeps it reachable on touch,
            // where there is no hover to reveal it at all.
            className={cn(
              'text-accent inline-flex items-center gap-1 rounded-sm opacity-0 transition-opacity',
              'group-hover:opacity-100 focus-visible:opacity-100 max-md:opacity-100',
              FOCUS_RING,
            )}
          >
            Open
            <ExternalLink className="size-3" strokeWidth={1.5} />
          </NavLink>
        </div>
      </div>
      {!frozen && onRemove && (
        <button
          type="button"
          onClick={onRemove}
          // size-7 rather than p-0.5 around a 14px icon: that was an 18px target for
          // the one destructive action on the card.
          className={cn(
            'text-dim hover:text-foreground hover:bg-hover flex size-7 shrink-0 items-center justify-center',
            'rounded-lg opacity-0 transition-opacity',
            'group-hover:opacity-100 focus-visible:opacity-100 max-md:opacity-100',
            FOCUS_RING,
          )}
          aria-label={`Remove ${experiment.name} from this stage`}
          title={`Remove ${experiment.name} from this stage`}
        >
          <X className="size-3.5" strokeWidth={1.5} />
        </button>
      )}
    </div>
  </div>
);
